<template>
  <transition-group
    name="panic-banner"
    tag="div"
    class="panic-banner-stack"
  >
    <div
      v-for="(alert, idx) in visibleBanners"
      :key="alert.id"
      class="panic-banner"
      :style="{ top: (topBaseOffset + idx * 76) + 'px' }"
      role="button"
      tabindex="0"
      @click="centrar(alert)"
      @keydown.enter="centrar(alert)"
      @keydown.space.prevent="centrar(alert)"
      @mouseenter="pauseAutoDismiss(alert.id)"
      @mouseleave="resumeAutoDismiss(alert.id)"
    >
      <div class="panic-banner__icon">
        <v-icon dark size="22">{{ bannerIcon(alert) }}</v-icon>
      </div>
      <div class="panic-banner__body">
        <div class="panic-banner__title">
          {{ bannerTitle(alert) }}
          <span class="panic-banner__meta">· {{ alertSourceLabel(alert) }} · {{ relativeTime(alert) }}</span>
        </div>
        <div class="panic-banner__subtitle">
          {{ alert.title }}
        </div>
      </div>
      <div class="panic-banner__actions">
        <v-btn
          color="white"
          light
          x-small
          class="mr-1"
          @click.stop="centrar(alert)"
        >
          <v-icon left x-small>mdi-crosshairs-gps</v-icon>
          Centrar
        </v-btn>
        <v-btn
          text
          dark
          x-small
          @click.stop="descartar(alert)"
        >
          Descartar
        </v-btn>
      </div>
    </div>
  </transition-group>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { isMcAlert, mcAlertLabel, mcAlertIcon } from '@/components/map-v2/data/mc-alert-types.js'

// Una alerta de medida cautelar viaja como hit; el subtipo va en data.
function esAlertaMc(a) {
  return !!(a && a.data && isMcAlert(a.data.typeOfItem))
}

// Tipos de alerta que disparan el banner superior rojo. Incluye incidentes
// 9-1-1 además de sensores/bus/patrulla para que el operador no se pierda una
// emergencia nueva por estar en otra vista. `device_alert` = panic del chofer
// de una patrulla (evento backend type=5).
const PANIC_TYPES = ['sensor_alert', 'external_sensor_alert', 'bus_alert', 'device_alert', 'incident']
const MAX_BANNERS = 5
// Auto-dismiss del banner si el operador no interactúa. El alerta sigue viva
// en el sidebar y en el mapa; solo se oculta el banner rojo superior.
const AUTO_DISMISS_MS = 10000

// Metadata por tipo de alerta: título del banner, label del source y el
// icono. Mantiene el resto del componente agnóstico del tipo.
const BANNER_META = {
  sensor_alert:          { title: 'Botón de Pánico Activado', source: 'Sensor',         icon: 'mdi-alarm-light' },
  external_sensor_alert: { title: 'Botón de Pánico Activado', source: 'Sensor externo', icon: 'mdi-alarm-light' },
  bus_alert:             { title: 'Botón de Pánico Activado', source: 'Unidad',         icon: 'mdi-alarm-light' },
  device_alert:          { title: 'Botón de Pánico Activado', source: 'Patrulla',       icon: 'mdi-alarm-light' },
  incident:              { title: 'Incidente 9-1-1',          source: 'Incidente',      icon: 'mdi-shield-alert' },
}

export default {
  name: 'PanicBanner',

  props: {
    alerts: { type: Array, default: () => [] },
    enabled: { type: Boolean, default: false },
  },

  data() {
    return {
      // null hasta que enabled flipea a true; despues guarda el snapshot
      // de IDs historicos que NO deben mostrar banner.
      snapshotIds: null,
      dismissedIds: {},
      nowTick: Date.now(),
      tickInterval: null,
      audio: null,
    }
  },

  computed: {
    ...mapGetters('sos-call-v2', { sosBannerQueue: 'getBannerQueue' }),
    ...mapState('map-v2', ['bannerAlertTypes']),

    panicAlerts() {
      var enabled = this.bannerAlertTypes || {}
      return this.alerts.filter(a => {
        // Las de medida cautelar llegan como 'hit' (se manejan igual que un
        // hit de LPR, por requisito de la reunion), pero una fuga de arraigo
        // no puede quedar sepultada en la lista lateral entre decenas de
        // detecciones: exige la misma franja roja que un boton de panico.
        if (esAlertaMc(a)) return enabled.mc_alert !== false
        if (PANIC_TYPES.indexOf(a.type) === -1) return false
        // Si el tipo fue desactivado en la configuracion del toolbar, no
        // sale el banner flotante (pero sigue entrando al sidebar y al mapa).
        return enabled[a.type] !== false
      })
    },

    visibleBanners() {
      if (!this.enabled || !this.snapshotIds) return []
      const out = []
      for (let i = 0; i < this.panicAlerts.length && out.length < MAX_BANNERS; i++) {
        const a = this.panicAlerts[i]
        if (this.snapshotIds[a.id]) continue
        if (this.dismissedIds[a.id]) continue
        out.push(a)
      }
      return out
    },

    // Stack debajo de los banners SOS si los hay, para que ambos coexistan
    // sin solaparse en la franja superior.
    topBaseOffset() {
      const sosCount = (this.sosBannerQueue || []).length
      return 56 + sosCount * 76
    },
  },

  watch: {
    enabled: {
      handler(val) {
        if (val && !this.snapshotIds) {
          // Captura las alertas historicas (initial load) para que no se
          // muestren como banner. Solo las que lleguen DESPUES de este punto
          // dispararan el banner — paridad con el `if (!this.loading)` legacy.
          const snap = {}
          for (let i = 0; i < this.panicAlerts.length; i++) {
            snap[this.panicAlerts[i].id] = true
          }
          this.snapshotIds = snap
        }
      },
      immediate: true,
    },

    visibleBanners(newVal) {
      if (newVal.length > 0) {
        this._playAudio()
      } else {
        this._stopAudio()
      }
      // Auto-dismiss: programa un timer por cada banner visible que no lo
      // tenga ya. Si un banner se va de la lista por otro camino (p.ej.
      // descartar manual), su timer se limpia abajo.
      this._syncAutoDismissTimers(newVal)
    },
  },

  mounted() {
    this.audio = new Audio('/sounds/sos-alert.wav')
    this.audio.loop = true
    this.tickInterval = setInterval(() => { this.nowTick = Date.now() }, 1000)
  },

  beforeDestroy() {
    if (this.tickInterval) clearInterval(this.tickInterval)
    this._stopAudio()
    this.audio = null
    // Limpieza de todos los timers de auto-dismiss pendientes.
    if (this._autoDismissTimers) {
      var ids = Object.keys(this._autoDismissTimers)
      for (var i = 0; i < ids.length; i++) clearTimeout(this._autoDismissTimers[ids[i]])
      this._autoDismissTimers = {}
    }
  },

  methods: {
    relativeTime(alert) {
      // eslint-disable-next-line no-unused-expressions
      this.nowTick
      const created = alert && alert.createdAt
      if (!created) return ''
      const diffMs = Date.now() - new Date(created).getTime()
      if (Number.isNaN(diffMs)) return ''
      const s = Math.floor(diffMs / 1000)
      if (s < 60) return `hace ${s}s`
      const m = Math.floor(s / 60)
      if (m < 60) return `hace ${m}m`
      const h = Math.floor(m / 60)
      return `hace ${h}h`
    },

    alertSourceLabel(alert) {
      if (esAlertaMc(alert)) return 'Medida cautelar'
      var meta = BANNER_META[alert && alert.type]
      return meta ? meta.source : 'Alerta'
    },

    // En las de medida cautelar el motivo manda —no es lo mismo salirse del
    // arraigo que quedarse sin bateria— asi que el titulo lo da el subtipo.
    bannerTitle(alert) {
      if (esAlertaMc(alert)) return mcAlertLabel(alert.data.typeOfItem)
      var meta = BANNER_META[alert && alert.type]
      return meta ? meta.title : 'Alerta'
    },

    bannerIcon(alert) {
      if (esAlertaMc(alert)) return mcAlertIcon(alert.data.typeOfItem)
      var meta = BANNER_META[alert && alert.type]
      return meta ? meta.icon : 'mdi-alert'
    },

    centrar(alert) {
      this.$emit('alert-click', alert)
      this._dismissBanner(alert.id)
    },

    descartar(alert) {
      this._dismissBanner(alert.id)
    },

    _dismissBanner(id) {
      this.$set(this.dismissedIds, id, true)
      if (this._autoDismissTimers && this._autoDismissTimers[id]) {
        clearTimeout(this._autoDismissTimers[id])
        delete this._autoDismissTimers[id]
      }
    },

    // Sincroniza los timers de auto-dismiss con la lista actual de banners:
    //   - IDs nuevos en la lista → arrancar timer 5 s.
    //   - IDs que ya no están → limpiar su timer (p.ej. descartados manual,
    //     limpiados por el store, etc).
    _syncAutoDismissTimers(banners) {
      if (!this._autoDismissTimers) this._autoDismissTimers = {}
      if (!this._autoDismissPaused) this._autoDismissPaused = {}
      var seen = {}
      for (var i = 0; i < banners.length; i++) {
        var id = banners[i].id
        seen[id] = true
        // No re-programar si ya tiene timer O si está pausado (hover activo).
        if (this._autoDismissTimers[id] || this._autoDismissPaused[id]) continue
        this._autoDismissTimers[id] = setTimeout(this._autoDismissHandler.bind(this, id), AUTO_DISMISS_MS)
      }
      var active = Object.keys(this._autoDismissTimers)
      for (var j = 0; j < active.length; j++) {
        var tid = active[j]
        if (!seen[tid]) {
          clearTimeout(this._autoDismissTimers[tid])
          delete this._autoDismissTimers[tid]
        }
      }
    },

    _autoDismissHandler(id) {
      delete this._autoDismissTimers[id]
      this.$set(this.dismissedIds, id, true)
    },

    // Mouse sobre el banner → pausa el auto-dismiss para que el operador
    // tenga tiempo de leer y decidir sin presión del timer.
    pauseAutoDismiss(id) {
      if (!this._autoDismissPaused) this._autoDismissPaused = {}
      this._autoDismissPaused[id] = true
      if (this._autoDismissTimers && this._autoDismissTimers[id]) {
        clearTimeout(this._autoDismissTimers[id])
        delete this._autoDismissTimers[id]
      }
    },

    // Mouse sale del banner → reinicia los 5s completos (más generoso que
    // descontar lo que ya pasó; si mueve el mouse por error, le da tiempo).
    resumeAutoDismiss(id) {
      if (!this._autoDismissPaused) this._autoDismissPaused = {}
      delete this._autoDismissPaused[id]
      // Solo re-programar si el banner sigue vivo (no descartado manual).
      if (!this.dismissedIds[id]) {
        this._autoDismissTimers[id] = setTimeout(this._autoDismissHandler.bind(this, id), AUTO_DISMISS_MS)
      }
    },

    _playAudio() {
      if (!this.audio) return
      if (!this.audio.paused) return
      this.audio.loop = true
      try {
        const p = this.audio.play()
        if (p && p.catch) p.catch(() => { /* autoplay blocked */ })
      } catch (e) { /* ignore */ }
    },

    _stopAudio() {
      if (!this.audio) return
      try {
        this.audio.pause()
        this.audio.currentTime = 0
      } catch (e) { /* ignore */ }
    },
  },
}
</script>

<style scoped>
.panic-banner-stack {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100vw - 48px);
  width: min(720px, calc(100vw - 48px));
  z-index: 10000;
  pointer-events: none;
}

.panic-banner {
  position: absolute;
  left: 0;
  right: 0;
  height: 64px;
  background: rgba(208, 72, 72, 0.92);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #FFFFFF;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  pointer-events: auto;
  /* Todo el banner es clickeable (atiende el incidente = centrar + abrir
     drawer). Solo "Descartar" lo cierra. Hover pausa el auto-dismiss. */
  cursor: pointer;
  transition: background 0.15s, box-shadow 0.15s, transform 0.1s;
}

.panic-banner:hover {
  background: rgba(231, 76, 60, 0.95);
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.5);
}

.panic-banner:active {
  transform: scale(0.998);
}

.panic-banner__icon {
  flex: 0 0 auto;
  margin-right: 12px;
}

.panic-banner__body {
  flex: 1 1 auto;
  min-width: 0;
}

.panic-banner__title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panic-banner__meta {
  font-weight: 400;
  opacity: 0.85;
  margin-left: 6px;
}

.panic-banner__subtitle {
  font-size: 13px;
  opacity: 0.85;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panic-banner__actions {
  flex: 0 0 auto;
  margin-left: 12px;
  display: flex;
  align-items: center;
}

.panic-banner-enter-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}
.panic-banner-leave-active {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}
.panic-banner-enter {
  transform: translateY(-100%);
  opacity: 0;
}
.panic-banner-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
